import { useEffect, useRef, useState } from 'react';
import { CheckCircle, DownloadSimple, Funnel, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import type { ContactsTableColumnDef } from '../../config/contacts-table-columns';
import type { CustomFieldDefinition } from '../../types/custom-field-definitions.types';
import {
  buildContactsExportCsv,
  downloadContactsExportCsv,
} from '../../utils/contact-export-csv';
import {
  runContactExport,
  type ContactExportSnapshot,
} from '../../utils/contact-export-runner';

export type ExportContactsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Filtered total from the current table query (Confirm N). */
  totalCount: number;
  /** Trimmed search shown in filter summary; also part of snapshot.q */
  search: string;
  /** How many active filters to mention in the summary (chips / channel / advanced). */
  activeFilterCount: number;
  snapshot: ContactExportSnapshot;
  /** Visible table columns — export CSV headers/cells follow this set. */
  visibleColumns: ContactsTableColumnDef[];
  customFieldDefinitions?: CustomFieldDefinition[];
};

type ExportStep = 'confirm' | 'progress' | 'done' | 'failed';

const buildFilterSummary = (search: string, activeFilterCount: number): string => {
  const trimmed = search.trim();
  const parts: string[] = [];
  if (trimmed) parts.push(`search "${trimmed}"`);
  if (activeFilterCount > 0) {
    parts.push(`${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied`);
  }
  if (parts.length === 0) {
    return 'No search or filters applied — exporting all contacts.';
  }
  return `Honoring: ${parts.join(' · ')}`;
};

export const ExportContactsDialog = ({
  open,
  onOpenChange,
  totalCount,
  search,
  activeFilterCount,
  snapshot,
  visibleColumns,
  customFieldDefinitions = [],
}: ExportContactsDialogProps) => {
  const [step, setStep] = useState<ExportStep>('confirm');
  const [fetched, setFetched] = useState(0);
  const [progressTotal, setProgressTotal] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [exportedCount, setExportedCount] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const cancelledRef = useRef(false);
  const runIdRef = useRef(0);
  const exportColumnsRef = useRef(visibleColumns);
  const exportDefinitionsRef = useRef(customFieldDefinitions);

  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    runIdRef.current += 1;
    setStep('confirm');
    setFetched(0);
    setProgressTotal(null);
    setErrorMessage(null);
    setCsvText(null);
    setExportedCount(0);
    setCancelling(false);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && step === 'progress') {
      cancelledRef.current = true;
      setCancelling(true);
      // Ignore in-flight result after close so we don't flash Confirm/Done later.
      runIdRef.current += 1;
    }
    onOpenChange(nextOpen);
  };

  const startExport = async () => {
    cancelledRef.current = false;
    const runId = ++runIdRef.current;
    // Snapshot column settings at Start so mid-run toggles don't change the CSV.
    exportColumnsRef.current = visibleColumns;
    exportDefinitionsRef.current = customFieldDefinitions;
    setCancelling(false);
    setStep('progress');
    setFetched(0);
    setProgressTotal(null);
    setErrorMessage(null);
    setCsvText(null);

    const result = await runContactExport({
      snapshot,
      isCancelled: () => cancelledRef.current,
      onProgress: ({ fetched: nextFetched, total }) => {
        if (runId !== runIdRef.current) return;
        setFetched(nextFetched);
        setProgressTotal(total);
      },
    });

    if (runId !== runIdRef.current) return;

    if (result.status === 'completed') {
      setCsvText(
        buildContactsExportCsv(result.contacts, {
          columns: exportColumnsRef.current,
          customFieldDefinitions: exportDefinitionsRef.current,
        })
      );
      setExportedCount(result.contacts.length);
      setFetched(result.contacts.length);
      setProgressTotal(result.total);
      setCancelling(false);
      setStep('done');
      return;
    }

    if (result.status === 'cancelled') {
      setCancelling(false);
      setStep('confirm');
      setFetched(0);
      setProgressTotal(null);
      return;
    }

    setCancelling(false);
    setErrorMessage(result.error.message || 'Export failed');
    setStep('failed');
  };

  const handleCancelDuringProgress = () => {
    cancelledRef.current = true;
    setCancelling(true);
  };

  const progressDenominator = progressTotal || totalCount || 1;
  const progressPercent = Math.min(100, Math.round((fetched / progressDenominator) * 100));
  const contactLabel = totalCount === 1 ? 'contact' : 'contacts';
  const filterSummary = buildFilterSummary(search, activeFilterCount);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[440px] gap-0 overflow-hidden p-0 sm:rounded-lg">
        <DialogHeader className="space-y-0 border-b border-neutral-200 px-5 py-4">
          <DialogTitle className="text-lg font-semibold text-neutral-900">
            Export contacts
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 px-5 py-5">
          {step === 'confirm' ? (
            <>
              <p className="text-neutral-900">
                This will export <strong>{totalCount}</strong> {contactLabel} to CSV, using the
                columns currently shown in the table.
              </p>
              <div className="flex items-start gap-2 rounded-lg bg-neutral-100 px-3 py-2.5 text-[13px] leading-[18px] text-neutral-900">
                <Funnel className="mt-0.5 size-3.5 shrink-0 text-neutral-600" aria-hidden />
                <span>{filterSummary}</span>
              </div>
              {totalCount === 0 ? (
                <p className="text-[13px] leading-[18px] text-neutral-500">
                  No contacts match the current search and filters.
                </p>
              ) : null}
            </>
          ) : null}

          {step === 'progress' ? (
            <>
              <p className="font-medium text-neutral-900">
                {fetched === 0
                  ? 'Fetching contacts…'
                  : `Fetched ${fetched} of ${progressTotal ?? totalCount}…`}
              </p>
              <div className="h-2 overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="h-full bg-primary-600 transition-[width] duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-[13px] leading-[18px] text-neutral-500">
                Export runs in this browser session — this won&apos;t block the rest of the app.
              </p>
            </>
          ) : null}

          {step === 'done' ? (
            <div className="flex items-start gap-2.5 rounded-lg bg-emerald-50 px-3 py-2.5">
              <CheckCircle
                className="mt-0.5 size-4 shrink-0 text-emerald-700"
                weight="regular"
                aria-hidden
              />
              <p className="text-[13px] leading-[18px] text-emerald-900">
                {exportedCount} rows exported — ready to download.
              </p>
            </div>
          ) : null}

          {step === 'failed' ? (
            <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-3 py-2.5">
              <WarningCircle
                className="mt-0.5 size-4 shrink-0 text-error"
                weight="regular"
                aria-hidden
              />
              <div className="space-y-1 text-[13px] leading-[18px] text-rose-900">
                {errorMessage ? <p>{errorMessage}</p> : null}
                <p>Your filters weren&apos;t lost — try again.</p>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex-row justify-end gap-2 border-t border-neutral-200 px-5 py-4 sm:justify-end">
          {step === 'confirm' || step === 'progress' ? (
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4"
              disabled={step === 'progress' && cancelling}
              onClick={() => {
                if (step === 'progress') {
                  handleCancelDuringProgress();
                  return;
                }
                onOpenChange(false);
              }}
            >
              {step === 'progress' && cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          ) : null}

          {step === 'confirm' ? (
            <Button
              type="button"
              className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
              disabled={totalCount === 0}
              onClick={() => {
                void startExport();
              }}
            >
              Start export
            </Button>
          ) : null}

          {step === 'failed' ? (
            <Button
              type="button"
              className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
              onClick={() => {
                void startExport();
              }}
            >
              Retry
            </Button>
          ) : null}

          {step === 'done' ? (
            <Button
              type="button"
              className="h-10 gap-1.5 bg-primary-600 px-4 text-white hover:bg-primary-700"
              onClick={() => {
                if (csvText) downloadContactsExportCsv(csvText);
              }}
            >
              <DownloadSimple className="size-4" aria-hidden />
              Download CSV
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
